import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { mapComplaintToRcaQueue, mapRca, type CmtsRcaVm } from "../mappers/rcaCapaMappers.js";
import { validateRca } from "../validators/rcaValidators.js";
import { repositoryCall, type RepositoryResult } from "./repositoryResult.js";
import { mapCollection } from "./repositoryCollection.js";
import { requireCmtsGuid } from "../utils/cmtsGuid.js";

export type RcaRepository = {
  queue(tenantId?: string, query?: Record<string, unknown>): Promise<RepositoryResult<readonly CmtsRcaVm[]>>;

  list(complaintId: string, tenantId?: string): Promise<RepositoryResult<readonly CmtsRcaVm[]>>;
  create(complaintId: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<CmtsRcaVm>>;
  complete(complaintId: string, rcaId: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<CmtsRcaVm>>;
};

function queueQuery(tenantId?: string, query: Record<string, unknown> = {}): Record<string, string | number | boolean | undefined> {
  const rawSearch = typeof query.search === "string" ? query.search : typeof query.searchText === "string" ? query.searchText : undefined;
  return {
    tenantId,
    search: rawSearch,
    page: typeof query.page === "number" ? query.page : typeof query.pageNumber === "number" ? query.pageNumber : 1,
    pageSize: typeof query.pageSize === "number" ? query.pageSize : 100,
  };
}

export function createRcaRepository(client: CmtsHttpClient): RcaRepository {
  return {
    queue: (tenantId, query = {}) => repositoryCall(async () => {
      const [realRecords, complaints] = await Promise.all([
        mapCollection(await client.get<unknown>(CMTS_API.complaints.rcaQueue(), { query: { tenantId } }), mapRca),
        mapCollection(await client.get<unknown>(CMTS_API.complaints.list, { query: queueQuery(tenantId, query) }), mapComplaintToRcaQueue),
      ]);
      const complaintIdsWithRca = new Set(realRecords.map((r) => r.complaintId).filter(Boolean));
      const pendingOnly = complaints.filter((c) => !complaintIdsWithRca.has(c.complaintId));
      return [...realRecords, ...pendingOnly];
    }, "CMTS_RCA_QUEUE"),
    list: (id, tenantId) => repositoryCall(async () => {
      const complaintId = requireCmtsGuid(id, "RCA list complaintId");
      return mapCollection(await client.get<unknown>(CMTS_API.complaints.rca(complaintId), { query: { tenantId } }), mapRca);
    }, "CMTS_RCA_LIST"),
    create: (id, payload, tenantId) => repositoryCall(async () => {
      const complaintId = requireCmtsGuid(id, "RCA create complaintId");
      const d = validateRca(payload);
      if (d.length) throw { success: false, message: "RCA validation failed", errors: d.map((x) => ({ code: x.code, field: x.field, message: x.message })) };
      return mapRca(await client.post<unknown>(CMTS_API.complaints.rca(complaintId), { ...payload, tenantId, complaintId }, { query: { tenantId } }));
    }, "CMTS_RCA_CREATE"),
    complete: (id, rcaId, payload, tenantId) => repositoryCall(async () => {
      const complaintId = requireCmtsGuid(id, "RCA complete complaintId");
      const backendRcaId = requireCmtsGuid(rcaId, "RCA complete rcaId");
      return mapRca(await client.post<unknown>(CMTS_API.complaints.completeRca(complaintId, backendRcaId), payload, { query: { tenantId } }));
    }, "CMTS_RCA_COMPLETE")
  };
}
